import React, { useState } from 'react';
import { Layout, Card, Row, Col, Typography, Spin, Statistic, DatePicker, Button, Space, message } from 'antd';
import { 
    TrophyOutlined, 
    FilePdfOutlined,
    FileExcelOutlined,
    SearchOutlined,
    ClearOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import axiosClient from '../../api/axiosClient';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ReportsPDF from './ReportsPDF';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Title: AntTitle } = Typography;
const { RangePicker } = DatePicker;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TrainerReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [trainerData, setTrainerData] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            let queryParams = "";
            if (dateRange[0] && dateRange[1]) {
                const start = dateRange[0].format('YYYY-MM-DD');
                const end = dateRange[1].format('YYYY-MM-DD');
                queryParams = `?startDate=${start}&endDate=${end}`;
            }

            const res = await axiosClient.get(`/reports/get_trainer_reports.php${queryParams}`);
            
            if (res.data.status === 'success') {
                setTrainerData(res.data.data);
                message.success("Trainer report generated.");
            }
        } catch (error) {
            console.error("Error fetching trainer report:", error);
            message.error("Failed to fetch report.");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setDateRange([null, null]);
        setTrainerData(null);
    };

    const handleExportExcel = () => {
        if (!trainerData) return;
        const wb = XLSX.utils.book_new();
        const sheetData = (trainerData.topTrainers || []).map(item => ({
             Trainer: item.name,
             Assigned_Members: item.count
        }));
        const ws = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(wb, ws, "Top Trainers");
        
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'});
        saveAs(data, "TrainerReport.xlsx");
    };

    const workloadChartData = {
        labels: trainerData?.topTrainers.map(item => item.name) || [],
        datasets: [
            {
                label: 'Assigned Members',
                data: trainerData?.topTrainers.map(item => item.count) || [],
                backgroundColor: 'rgba(255, 159, 64, 0.6)',
            }
        ]
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Headerdiv />
            <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
                <div style={{ marginBottom: 20 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Back to Reports</Button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <AntTitle level={2} style={{ margin: 0 }}><TrophyOutlined /> Trainer Reports</AntTitle>
                    <Space>
                        <RangePicker onChange={setDateRange} value={dateRange} />
                        <Button type="primary" icon={<SearchOutlined />} onClick={fetchReport} loading={loading}>Fetch</Button>
                        <Button icon={<ClearOutlined />} onClick={handleClear} disabled={loading}>Clear</Button>
                        
                        {trainerData && (
                            <>
                                <PDFDownloadLink
                                    document={<ReportsPDF trainer={trainerData} dateRange={dateRange} />}
                                    fileName="TrainerReport.pdf"
                                >
                                    {({ loading: pdfLoading }) => (
                                        <Button danger icon={<FilePdfOutlined />} loading={pdfLoading}>PDF</Button>
                                    )}
                                </PDFDownloadLink>

                                <Button 
                                    onClick={handleExportExcel}
                                    style={{ borderColor: '#217346', color: '#217346' }} 
                                    icon={<FileExcelOutlined />}
                                >
                                    Excel
                                </Button>
                            </>
                        )}
                    </Space>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', marginTop: 50 }}><Spin size="large" /></div>
                ) : trainerData ? (
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Card>
                                        <Statistic 
                                            title="Total Trainers" 
                                            value={trainerData.totalTrainers} 
                                            prefix={<TrophyOutlined />}
                                        />
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card>
                                        <Statistic 
                                            title="Active Trainers" 
                                            value={trainerData.activeTrainers} 
                                            valueStyle={{ color: '#3f8600' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </Col>
                        <Col span={24}>
                            <Card title="Top Trainers by Workload">
                                <Bar options={{ responsive: true }} data={workloadChartData} />
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    <div style={{ textAlign: 'center', marginTop: 100, color: '#999' }}>
                        <TrophyOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <p>No report loaded.</p>
                    </div>
                )}
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default TrainerReport;
